<template>
  <div>
    <h3 class="title is-3">Import activities</h3>
    <div style="padding-bottom: 30px">
      <p>Please use <strong><a href="/import/ImportTemplate.xlsx" target="_blank">this template</a></strong> to import
        activities. Don't forget to remove the first lines of examples.</p>
    </div>
    <b-field label="Import a file (format: XSLX)" v-if="!isLoaded">
      <b-upload type="file" @input="processRows" drag-drop
                v-model="excelFile"
                accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
      >
        <section class="section">
          <div class="content has-text-centered">
            <p>
              <b-icon
                  icon="upload"
                  size="is-large">
              </b-icon>
            </p>
            <p>Drop your file here or click to upload</p>
          </div>
        </section>
      </b-upload>
    </b-field>
    <div v-if="isLoaded">
      <b-table :data="tableRows">
        <template slot-scope="props">
          <b-table-column class="centered-table-content" field="Name" label="Name">
            {{ props.row.Name }}
          </b-table-column>
          <b-table-column class="centered-table-content" field="Type" label="Type">
            {{ props.row.Type }}
          </b-table-column>
          <b-table-column class="centered-table-content" field="Question" label="Question">
            {{ props.row.Question }}
          </b-table-column>
          <b-table-column class="centered-table-content" field="Answer Type" label="Answer Type">
            {{ props.row['Answer Type'] }}
          </b-table-column>
          <b-table-column class="centered-table-content" field="Unlocked" label="Unlocked">
            {{ props.row.Unlocked }}
          </b-table-column>
          <b-table-column class="centered-table-content" field="Time To Answer" label="Time To Answer">
            {{ props.row['Time To Answer'] }}
          </b-table-column>
          <b-table-column class="centered-table-content" field="tags" label="Tags">
            <b-taglist>
              <b-tag
                  v-for="(tag, index) in generateTags(props.row.Tags)"
                  :key="index"
                  type="is-info">{{ tag }}
              </b-tag>
            </b-taglist>
          </b-table-column>
        </template>
      </b-table>
    </div>

    <div class="has-text-centered" v-if="isLoaded">
      <b-button
          @click.prevent="isLoaded=false"
          icon-left="restart"
          class="btn-margin"
      >
        Restart
      </b-button>
      <b-button @click="importData"
                icon-left="upload"
                type="is-success"
      >
        Import {{ rows.length - 1 }} activities
      </b-button>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import readXlsxFile from 'read-excel-file'
import displayHelper from '../helpers/display-helper'
import activityTypesHelper from '@/enums/activityTypes'
import rawActivityService from '../services/rawActivityService'
import constantHelper from '@/enums/constants'

const SHEET_QUIZ_NUMBER = 2

export default {
  name: 'RawActivitiesImport',
  data() {
    return {
      activeStep: 0,
      game: {},
      excelFile: {},
      isLoaded: false,
      rows: [],
      tableRows: [],
    }
  },
  created() {
  },
  methods: {
    processRows() {
      var parent = this
      readXlsxFile(this.excelFile, {sheet: SHEET_QUIZ_NUMBER}).then((rows) => {
        this.rows = rows
        const header = rows[0]
        this.tableRows = _.drop(rows).map((r) => {
          return _.zipObject(header, r)
        })
        this.isLoaded = true
      })
          .catch(err => {
            console.error(err)
            displayHelper.displayError(parent, 'Error while deleting the activity: ' + err.message)
          })
    },
    importData() {
      this.isLoading = true
      let allSuccess = true

      // these 4 lines of code do not work with serverless-offline and cls-hooked
      // (ie the tenantId is not injected in all lines when done simultaneously)
      // use a "for row of this.tableRows" loop instead when using the local server
      const createPromiseArray = this.tableRows.map((row) => {
        return this.createRawActivity(row)
      })
      return Promise.all(createPromiseArray)
          .then((successTab) => {
            console.dir(successTab)

            if (_.indexOf(successTab, false) > -1) {
              allSuccess = false
            }

            this.isLoading = false
            console.log(`allSuccess dehors = ${allSuccess} `)
            if (allSuccess) {
              displayHelper.displaySuccess(this, 'The activities have successfully been imported')
              this.$router.back()
            } else {
              displayHelper.displayError(this, 'Oops, some activities may have not been imported properly. Check the logs')
            }
          })
    },
    async createRawActivity(row) {
      let contentToAdd = {}

      switch (row.Type) {
        case activityTypesHelper.ACTIVITY_TYPES.PHOTO:
          contentToAdd = {
            question: row.Question,
            type: 'picture',
            photoActivityType: 'Photo',
            unlocked_forced: row.Unlocked === 'Yes',
          }
          break
        case activityTypesHelper.ACTIVITY_TYPES.UNLOCK_ONLY:
          contentToAdd = {
            question: row.Question,
            type: 'Unlock',
            unlocked_forced: false,
          }
          break
        case activityTypesHelper.ACTIVITY_TYPES.QUIZ:
          contentToAdd = {
            question: row.Question,
            type: row['Answer Type'] !== 'Free Text' ? 'one-answer' : 'free-text',
            correctAnswer: row['Answer Type'] !== 'Free Text' ? row['Correct Answer Number'].toString() : row['Free Text Answer'].toString(),
            answers: row['MultiChoices Answers'] ? this.transformAnswersForServer(row['MultiChoices Answers'].split(',')) : {},
            explanation: row.Explanation,
            unlocked_forced: row.Unlocked === 'Yes',
          }
          break
        default:
          // this should never happen
          console.log('Activity type unknown: ' + row.Type)
          return false
      }

      const rawActivity = {
        name: row.Name,
        activityType: row.Type,
        content: contentToAdd,
        allowedAttempts: row['Allowed Attempts'] ? row['Allowed Attempts'] : '',
        points: row.Points ? parseInt(row.Points) : 100,
        imgPartOfTheQuestion: row['Image Part of Question'] === 'Yes',
        difficulty: row.Difficulty ? parseInt(_.head(row.Difficulty)) : 1,
        tags: row.Tags ? row.Tags.split(',') : [],
        timeToAnswer: row['Time To Answer'] ? row['Time To Answer'] : 0,

        imgSrc: row.imgSrc ? row.imgSrc : constantHelper.GENERIC_PHOTO_URL,
      }

      const creationRawActivityResult = await rawActivityService.createRawActivity(rawActivity, null)
      return creationRawActivityResult.isSuccess
    },
    transformAnswersForServer(answers) {
      const tab = _.zip(_.range(1, answers.length + 1), answers)
      return _.fromPairs(tab)
    },
    generateTags(tags) {
      return tags ? tags.split(',') : []
    },
  },
}
</script>

<style scoped>
.btn-margin {
  margin-left: 10px;
  margin-right: 10px;
}
</style>
